/**
 * ============================================================
 *  新增文件 -- 文章搜索 (blog-search.js)
 * ============================================================
 *
 * <blog-search>
 * 单 blog 内的客户端文章搜索：用 Liquid 内联的 JSON 索引，按
 * title / excerpt / tags 做大小写不敏感的多词 AND 匹配。
 *
 * 零依赖（Web Components + 原生 DOM）。100 篇 / 仅元数据规模下，
 * 单次过滤耗时远小于 1 ms，移动端也无感。
 *
 * 与 blog-tags.js 协作：监听 `blog:content-updated` 事件，在
 * 标签 AJAX 切换后自动重建 DOM 引用并按当前关键词重跑。
 */
if (!customElements.get("blog-search")) {
  customElements.define(
    "blog-search",
    class BlogSearch extends HTMLElement {
      constructor() {
        super();
        this.debounceTimer = null;
        this.lastTerm = "";
      }

      connectedCallback() {
        this.input = this.querySelector("[data-blog-search-input]");
        this.clearBtn = this.querySelector("[data-blog-search-clear]");

        if (!this.input) return;

        this.showDate = this.dataset.showDate === "true";
        this.showAuthor = this.dataset.showAuthor === "true";
        this.showTags = this.dataset.showTags === "true";
        this.showExcerpt = this.dataset.showExcerpt === "true";
        this.ratioPercent = this.computeRatio(this.dataset.imageRatio);

        this.refreshContentRefs();
        this.bindEvents();
        this.syncFromQuery();
      }

      /**
       * Re-resolve all DOM references that live inside #Blog-Content.
       * Called on first init and again every time blog-tags.js replaces
       * the content via AJAX (tag filter switch).
       */
      refreshContentRefs() {
        this.defaultView = this.querySelector("[data-blog-search-default]");
        this.resultsWrap = this.querySelector("[data-blog-search-results]");
        this.resultsList = this.querySelector("[data-blog-search-results-list]");
        this.emptyMsg = this.querySelector("[data-blog-search-empty]");
        this.statusEl = this.querySelector("[data-blog-search-status]");
        this.dataScript = this.querySelector("[data-blog-search-data]");
        this.parseIndex();
      }

      parseIndex() {
        if (!this.dataScript) {
          this.articles = [];
          return;
        }
        try {
          const raw = JSON.parse(this.dataScript.textContent || "[]");
          this.articles = raw.map((a) => ({
            ...a,
            _haystack: [
              a.title || "",
              a.excerpt || "",
              Array.isArray(a.tags) ? a.tags.join(" ") : "",
            ]
              .join(" \u0001 ")
              .toLowerCase(),
          }));
        } catch (e) {
          this.articles = [];
          console.warn("[blog-search] failed to parse article index", e);
        }
      }

      bindEvents() {
        this.input.addEventListener("input", () => {
          window.clearTimeout(this.debounceTimer);
          this.debounceTimer = window.setTimeout(() => this.run(), 150);
        });

        this.input.addEventListener("search", () => this.run());

        if (this.clearBtn) {
          this.clearBtn.addEventListener("click", () => {
            this.input.value = "";
            this.run();
            this.input.focus();
          });
        }

        // Sync ?bq=... back/forward navigation
        window.addEventListener("popstate", () => this.syncFromQuery());

        // blog-tags.js AJAX-swaps #Blog-Content when the user picks a tag.
        // All our content-side refs (JSON script, results container, status
        // text, default paginated view) get detached and replaced. We need
        // to re-resolve them and re-apply any active query against the new
        // (tag-filtered) dataset.
        document.addEventListener("blog:content-updated", () => {
          this.refreshContentRefs();
          this.lastTerm = "";
          this.run({ skipUrl: true });
        });
      }

      syncFromQuery() {
        const params = new URLSearchParams(window.location.search);
        const term = params.get("bq") || "";
        if (term && term !== this.input.value) {
          this.input.value = term;
        }
        this.run({ skipUrl: true });
      }

      run(opts = {}) {
        const term = (this.input.value || "").trim();
        if (term === this.lastTerm && !opts.force) return;
        this.lastTerm = term;

        this.toggleClear(term.length > 0);

        if (!opts.skipUrl) this.updateUrl(term);

        if (!term) {
          this.showDefaultView();
          return;
        }

        const tokens = term.toLowerCase().split(/\s+/).filter(Boolean);
        const matches = this.articles.filter((a) =>
          tokens.every((t) => a._haystack.includes(t))
        );

        this.renderResults(matches, term);
      }

      showDefaultView() {
        if (this.defaultView) this.defaultView.classList.remove("hidden");
        if (this.resultsWrap) this.resultsWrap.classList.add("hidden");
        if (this.statusEl) {
          const defaultText = this.statusEl.dataset.defaultText || "";
          this.statusEl.textContent = defaultText;
        }
      }

      renderResults(matches, term) {
        if (this.defaultView) this.defaultView.classList.add("hidden");
        if (this.resultsWrap) this.resultsWrap.classList.remove("hidden");

        if (this.statusEl) {
          this.statusEl.textContent = this.formatCount(matches.length, term);
        }

        if (!this.resultsList) return; // empty tag page, nothing to render into

        if (matches.length === 0) {
          this.resultsList.innerHTML = "";
          if (this.emptyMsg) {
            this.emptyMsg.textContent = this.formatNoResults(term);
            this.emptyMsg.classList.remove("hidden");
          }
          return;
        }

        if (this.emptyMsg) this.emptyMsg.classList.add("hidden");

        const frag = document.createDocumentFragment();
        matches.forEach((article) => {
          frag.appendChild(this.buildCard(article, term));
        });
        this.resultsList.innerHTML = "";
        this.resultsList.appendChild(frag);

        if (typeof webvista !== "undefined" && webvista.initLazyImages) {
          webvista.initLazyImages();
        }
      }

      buildCard(article, term) {
        const li = document.createElement("li");
        li.className = "grid-item focus-inset";
        li.tabIndex = 0;

        const wrap = document.createElement("div");
        wrap.className = "article-card-wrapper card-wrapper hover-trigger";

        const card = document.createElement("div");
        card.className = "card";

        // Media
        const inner = document.createElement("div");
        inner.className = "card-inner ratio";
        inner.style.setProperty("--ratio-percent", this.ratioPercent);
        const mediaWrap = document.createElement("div");
        mediaWrap.className = "card-media-wrapper";
        const media = document.createElement("div");
        media.className = "card-media media";

        if (article.image) {
          const img = document.createElement("img");
          img.loading = "lazy";
          img.decoding = "async";
          img.src = article.image;
          img.alt = article.image_alt || article.title || "";
          img.className = "animate-scale-up";
          media.appendChild(img);
        } else {
          const placeholder = document.createElement("div");
          placeholder.className = "placeholder-svg animate-scale-up";
          media.appendChild(placeholder);
        }

        mediaWrap.appendChild(media);
        inner.appendChild(mediaWrap);

        const mediaLink = document.createElement("a");
        mediaLink.href = article.url;
        mediaLink.className = "card-inner-content link";
        mediaLink.setAttribute("aria-label", article.title || "");
        mediaLink.tabIndex = -1;
        inner.appendChild(mediaLink);

        card.appendChild(inner);

        // Content
        const content = document.createElement("div");
        content.className = "card-content";

        const caption = document.createElement("div");
        caption.className = "card-caption caption";
        if (this.showDate && article.date_display) {
          const dateSpan = document.createElement("span");
          dateSpan.textContent = article.date_display;
          caption.appendChild(dateSpan);
        }
        if (this.showAuthor && article.author) {
          const authorSpan = document.createElement("span");
          authorSpan.textContent = article.author;
          caption.appendChild(authorSpan);
        }
        if (caption.childNodes.length) content.appendChild(caption);

        const heading = document.createElement("h3");
        heading.className = "card-heading font-body";
        const titleLink = document.createElement("a");
        titleLink.href = article.url;
        titleLink.className = "link link-underline";
        titleLink.innerHTML = this.highlight(article.title || "", term);
        heading.appendChild(titleLink);
        content.appendChild(heading);

        if (this.showExcerpt && article.excerpt) {
          const desc = document.createElement("div");
          desc.className = "card-description light";
          desc.innerHTML = this.highlight(
            this.truncate(article.excerpt, 25),
            term
          );
          content.appendChild(desc);
        }

        card.appendChild(content);
        wrap.appendChild(card);
        li.appendChild(wrap);
        return li;
      }

      highlight(text, term) {
        const escaped = this.escapeHtml(text);
        if (!term) return escaped;
        const tokens = term
          .split(/\s+/)
          .filter(Boolean)
          .map((t) => this.escapeRegExp(t));
        if (!tokens.length) return escaped;
        const re = new RegExp(`(${tokens.join("|")})`, "gi");
        return escaped.replace(re, "<mark>$1</mark>");
      }

      truncate(text, words) {
        const parts = text.split(/\s+/);
        if (parts.length <= words) return text;
        return parts.slice(0, words).join(" ") + "\u2026";
      }

      escapeHtml(s) {
        return String(s)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");
      }

      escapeRegExp(s) {
        return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      }

      computeRatio(ratio) {
        switch (ratio) {
          case "portrait":
            return "125%";
          case "square":
            return "100%";
          case "landscape":
          default:
            return "62.5%";
        }
      }

      toggleClear(visible) {
        if (!this.clearBtn) return;
        this.clearBtn.classList.toggle("hidden", !visible);
      }

      updateUrl(term) {
        try {
          const url = new URL(window.location.href);
          if (term) {
            url.searchParams.set("bq", term);
          } else {
            url.searchParams.delete("bq");
          }
          window.history.replaceState({}, "", url.toString());
        } catch (e) {
          /* ignore */
        }
      }

      formatCount(count, term) {
        const tpl =
          count === 1
            ? this.dataset.i18nCountOne ||
              `1 result for \u201c__TERM__\u201d`
            : this.dataset.i18nCountOther ||
              `__COUNT__ results for \u201c__TERM__\u201d`;
        return tpl
          .replace(/__COUNT__/g, String(count))
          .replace(/__TERM__/g, term);
      }

      formatNoResults(term) {
        const tpl =
          this.dataset.i18nNoResults ||
          `No articles match \u201c__TERM__\u201d.`;
        return tpl.replace(/__TERM__/g, term);
      }
    }
  );
}
